import _ from "lodash";
import React, { useRef, useState } from "react";
import I18nHOC from "UIComponents/I18nHOC";
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";
import CriteriaRating from "UIComponents/CriteriaRating";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import AIJustification from "UIComponents/AIJustification";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { compose } from "lib/apolloClient";
import classes from "./RubricTable.scss";
import { connect } from "react-redux";
import classNames from "classnames";
import { colors } from "Constants";
import { getStyleStrippedText } from "Utils";
import { InfoIcon } from "SvgComponents";
import { getCriteriaSetOfPypType } from "modules/Services";
import { updateField } from "modules/FieldUpdateThunks";
import { writeAssessmentToolElementLocal } from "modules/AssessmentTool/AssessmentToolModule";
import {
  createPypElementRating,
  deletePypElementRating,
  createIbPypElementRating,
} from "AssessmentEvaluation/modules/AssessmentEvaluationModule";
import { TickCircleFilled, CircleOutlined } from "@toddle-edu/ds-icons";
import { getStudentDetailsWithArchivedStatus } from "ClassRoom/modules/utils";
import { getLabelBeforeColon } from "Utils/AssessmentToolHelpers";
import { saveAssessmentToolUpdateStatusInRedux } from "Assessments/modules/AssessmentModule";
import {
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { getMypIbDefinedCriteriaSet } from "Gradebook/modules/Utils";
import StudentListPopover from "UIComponents/StudentListPopover";
import { readMYPIBDefinedGradeScaleFromCache } from "GradingSetupV2/modules/GradingSetupV2GraphqlHelper";
import { convertMypGradeScaleToCriteriaSet } from "GradingSetupV2/modules/Utils";
import { USE_NEW_GRADING_SETUP_IN_COURSE_PERM } from "Constants/permissionConstants";
import { getIsConfigurationEnabledForCPAY } from "modules/PermissionModule";
import {
  getIsMediaManagerRoute,
  setElementRatingMutationInProgress,
} from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/ClassRoomModule";

const styles = {
  tooltipContainerStyle: {
    width: "500px",
  },
  customSelectedStyle: {
    backgroundColor: colors.blue400,
  },
  aiTick: {
    color: "var(--interactive-tertiary)",
  },
  marginBottom: {
    marginBottom: "8px",
  },
};
/*
  Todo : Handle Locking state
*/
const textAreaStyles = {
  cell: {
    padding: 0,
    color: colors.neutral300,
    lineHeight: 1.57,
    fontSize: "1.4rem",
    wordBreak: "normal",
  },
};

const MoreComponent = ({ studentArray, responseCount, insightColor, t }) => {
  const [showPopover, setShowPopover] = useState(false);
  const containerRef = useRef(null);

  const handleOnHover = async () => {
    if (_.isEmpty(studentArray)) return;
    setShowPopover(true);
  };

  const handleOnHoverOut = () => {
    setShowPopover(false);
  };
  return (
    <div
      ref={containerRef}
      onMouseEnter={handleOnHover}
      onMouseLeave={handleOnHoverOut}
      className={classes.insight}
    >
      <div className={classes.insightCount} style={{ color: insightColor }}>
        {responseCount}
      </div>
      <div className={classes.insightStudent}>
        {t("common:student", { count: responseCount })}
      </div>
      {showPopover ? (
        <StudentListPopover
          studentList={getStudentDetailsWithArchivedStatus({
            studentArray,
          })}
          popOverContainerClasses={classes.popoverContainer}
          containerRef={containerRef}
          position={["top"]}
        />
      ) : null}
    </div>
  );
};

class RubricTable extends React.PureComponent {
  constructor(props) {
    super(props);
    this.cellRefs = {};
    this.focusedCell = null;
    if (props.customRef) {
      props.customRef(this);
    }
    this.evaluationCellRefs = {};

    this.debouncedWriteRubricElementLocal = _.debounce(
      props.writeAssessmentToolElementLocal,
      1000
    );
  }

  blur = () => {
    if (this.focusedCell && this.cellRefs[this.focusedCell]) {
      this.cellRefs[this.focusedCell].blur();
    }
  };

  getHeaderRow = () => {
    const {
      achievementLevels,
      rubricHeaderRowCellStyle = {},
      showRevampedEvaluationUI,
      isEvaluationEnable,
      mode,
      isAiEvaluationEnabled,
      t,
    } = this.props;

    const showSubtleBackground = mode === "view" && isAiEvaluationEnabled;

    const headerRowCellClass = classNames({
      [classes.headerRowCell]: true,
      [classes.headerRowCellV2]: showSubtleBackground,
    });

    const headerRowColumnCellClass = classNames({
      [classes.headerRowColumnCell]: true,
      [classes.headerRowColumnCellV2]: showSubtleBackground,
    });

    if (showRevampedEvaluationUI && isEvaluationEnable) {
      return (
        <thead>
          <tr>
            <th className={classes.cellWrapper}>
              <div
                className={classes.headerRowColumnCell}
                style={rubricHeaderRowCellStyle}
              >
                {""}
              </div>
            </th>
            {_.map(achievementLevels, level => {
              return (
                <th key={level.id} className={classes.cellWrapper}>
                  <div
                    className={classes.headerRowCell}
                    style={rubricHeaderRowCellStyle}
                  >
                    {level.label}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
      );
    }

    return (
      <thead>
        <tr>
          <th
            className={headerRowColumnCellClass}
            style={rubricHeaderRowCellStyle}
            aria-label={t("common:criteria")}
          ></th>
          {_.map(achievementLevels, level => {
            return (
              <th
                key={level.id}
                className={headerRowCellClass}
                style={rubricHeaderRowCellStyle}
              >
                {level.label}
              </th>
            );
          })}
        </tr>
      </thead>
    );
  };

  renderSkeletonLoader = ({ count } = {}) => {
    return _.times(count, index => {
      return <SkeletonLoader key={index} width={44} height={32} />;
    });
  };

  /**
   * This function creates and returns the body elements of the MYP rubric table depending
   * on whether rating is enabled or not.
   */
  getTableBody = () => {
    const {
      isEvaluation,
      t,
      isEvaluationEnable,
      isEvaluated,
      userType,
      seggregatedObjectives,
      parentId,
      projectId,
      parentType,
      studentId,
      studentIds = [],
      ratingElementSubType,
      ibPYPElementRatings,
      createPypElementRating,
      createIbPypElementRating,
      deletePypElementRating,
      getCriteriaSetOfPypType,
      showInsight,
      rubricRatingContainerStyle,
      rubricRatingTextContainerStyle,
      rubricRatingCompContainerStyle,
      showRatingContainerInViewMode,
      subjectLabelStyle,
      objectiveNameStyle = {},
      ibPYPElementRatingsLocal,
      setIBPYPElementRatingsLocal,
      academicTermId,
      extraParams: { sourceFrom = "", queryVars = {}, projectGroupType } = {},
      criteriaRatingElementsContainerStyle,
      criteriaRatingCustomRatingElementClass,
      isAutoSaveDisabled = false,
      currentCurriculumProgramId,
      showObjectiveNameInTable = false,
      innerContainerStyle = {},
      showRevampedEvaluationUI,
      showOnlyDefaultCursor,
      isAiEvaluationEnabled,
      onAcceptAIEvaluation,
      mode,
      isGeneratingAIResponse,
      mypIbDefinedGradeScale,
      isUsingNewGradingSetupInCourses,
    } = this.props;

    const isStaff = userType === "staff";

    // currently we are picking the first criteria set as there is no distinguishing key
    // to filter out the appropriate criteria set. Later when such key is implemented then
    // we need to change this picking logic

    const mypObjectiveCriteriaSets = getCriteriaSetOfPypType({
      type: "MYP_OBJECTIVES",
      subType: ratingElementSubType,
    });

    const criteriaSet = isUsingNewGradingSetupInCourses
      ? convertMypGradeScaleToCriteriaSet({
          gradeScale: mypIbDefinedGradeScale,
          curriculumProgramId: currentCurriculumProgramId,
        })
      : getMypIbDefinedCriteriaSet({
          academicCriteriaSets: mypObjectiveCriteriaSets,
          curriculumProgramId: currentCurriculumProgramId,
        });

    const isAiEvaluationInViewMode = mode === "view" && isAiEvaluationEnabled;

    const tableWrapperClasses = classNames({
      [classes.tableWrapper]: true,
      [classes.tableWrapperV2]: showObjectiveNameInTable,
      [classes.tableWrapperWithoutBorder]:
        (showRevampedEvaluationUI && isEvaluationEnable) ||
        isAiEvaluationInViewMode,
    });

    const subjectLabelClasses = classNames({
      [classes.subjectLabel]: true,
      [classes.subjectLabelV2]: showObjectiveNameInTable,
    });

    const innerContainerClasses = classNames({
      [classes.innerContainer]: true,
      [classes.innerContainerV2]: isAiEvaluationInViewMode,
    });

    const academicCriteriaValues = _.get(
      criteriaSet,
      "academicCriteriaValues",
      []
    );

    const criteriaSetId = _.get(criteriaSet, "id", "");

    const seggregatedObjectivesValues = Object.values(seggregatedObjectives);

    return _.map(
      seggregatedObjectivesValues,
      (seggregatedObjectivesValue, index) => {
        const { label = "", children = [] } = seggregatedObjectivesValue || {};

        return isEvaluation ? (
          <React.Fragment key={index}>
            {label ? (
              <div className={subjectLabelClasses} style={subjectLabelStyle}>
                {label}
              </div>
            ) : null}
            {_.map(children, child => {
              return _.map(child, (objectives, index) => {
                const nonLeafObjective = _.first(
                  _.filter(objectives, objective => !objective.isLeaf)
                );

                const isFirstObjective = index === 0;

                const ibPypElementId = _.get(nonLeafObjective, "id", "");

                const aiSelectedCriteria = _.find(
                  ibPYPElementRatings,
                  rating => {
                    return (
                      _.get(rating, "ibPYPElementId") === ibPypElementId &&
                      _.get(rating, "creatorType") === "AI" &&
                      !_.get(rating, "isDeleted") &&
                      _.get(rating, "academicCriteriaSet.id") === criteriaSetId
                    );
                  }
                );

                const staffCriteria = _.find(ibPYPElementRatings, rating => {
                  return (
                    _.get(rating, "ibPYPElementId") === ibPypElementId &&
                    _.get(rating, "academicCriteriaSet.id") === criteriaSetId &&
                    _.get(rating, "creatorType", "STAFF") === "STAFF"
                  );
                });

                const aiCriteria = _.find(ibPYPElementRatings, rating => {
                  return (
                    _.get(rating, "ibPYPElementId") === ibPypElementId &&
                    _.get(rating, "creatorType") === "AI" &&
                    _.get(rating, "academicCriteriaSet.id") === criteriaSetId
                  );
                });

                const aiSelectedRating = _.find(
                  academicCriteriaValues,
                  value =>
                    value.id ===
                    _.get(aiCriteria, "academicCriteriaValue.id", "")
                )?.label;

                const aiSelectedRatingLabel = aiSelectedRating || "-";

                const criteriaLabel = getLabelBeforeColon(
                  nonLeafObjective.label
                );

                const aiJustificationlabel = `${criteriaLabel}: ${aiSelectedRatingLabel}/${
                  _.size(academicCriteriaValues) - 1
                }`;

                const selectedCriteria = isAiEvaluationEnabled
                  ? aiSelectedCriteria || staffCriteria
                  : staffCriteria || aiSelectedCriteria;

                const isAISelectedCriteria =
                  isAiEvaluationEnabled && aiSelectedCriteria;

                const justificationMessage = _.get(
                  aiCriteria,
                  "evaluationJustification.message",
                  ""
                );

                const handleCriteriaClick = criteriaId => {
                  const isDeleteAiRatings =
                    isAiEvaluationEnabled &&
                    selectedCriteria?.creatorType === "AI";

                  if (isDeleteAiRatings) {
                    onAcceptAIEvaluation({
                      ibPypElementId,
                      academicTermId,
                      academicCriteriaValueId: criteriaId,
                      academicCriteriaSetId: criteriaSetId,
                    });
                  }

                  if (
                    selectedCriteria &&
                    _.get(selectedCriteria, "academicCriteriaValue.id", "") ===
                      criteriaId &&
                    !isDeleteAiRatings
                  ) {
                    deletePypElementRating({
                      studentIds,
                      studentId,
                      projectId,
                      parentId,
                      parentType,
                      type: "MYP_OBJECTIVES",
                      id: ibPypElementId,
                      setId: criteriaSetId,
                      ibPYPElementRatingsLocal,
                      setIBPYPElementRatingsLocal,
                      sourceFrom,
                      queryVars,
                      projectGroupType,
                      academicTermId,
                      isAutoSaveDisabled,
                    });
                  } else {
                    const params = {
                      projectId,
                      parentId,
                      parentType,
                      studentId,
                      studentIds,
                      criteriaValue: criteriaId,
                      type: "MYP_OBJECTIVES",
                      id: ibPypElementId,
                      setId: criteriaSetId,
                      isPublished: isEvaluated,
                      criteriaSet,
                      academicCriteriaValue: _.find(academicCriteriaValues, {
                        id: criteriaId,
                      }),
                      academicTermId,
                      ibPYPElementRatingsLocal,
                      setIBPYPElementRatingsLocal,
                      sourceFrom,
                      queryVars,
                      isDeleteAiRatings,
                      projectGroupType,
                      isAutoSaveDisabled,
                      getIsMediaManagerRoute,
                      setElementRatingMutationInProgress,
                    };

                    if (parentType === "STUDENT_ASSIGNMENT") {
                      createIbPypElementRating(params);
                    } else {
                      createPypElementRating(params);
                    }
                  }
                };

                const customRatingElementClass = classNames({
                  [criteriaRatingCustomRatingElementClass]: !_.isEmpty(
                    criteriaRatingCustomRatingElementClass
                  ),
                  [classes.showWhiteBg]:
                    mode === "view" && isAiEvaluationEnabled,
                });

                const showJustification =
                  isStaff && isAiEvaluationEnabled && justificationMessage;

                const objectiveNameClass = classNames(
                  { [classes.objectiveName]: true },
                  { "text-label-2": !showObjectiveNameInTable },
                  { [classes.objectiveNameSpacing]: showInsight },
                  { [classes.objectiveNameV2]: showObjectiveNameInTable },
                  { "text-label": showObjectiveNameInTable },
                  {
                    [classes.objectiveWithJustification]:
                      !isFirstObjective && showJustification,
                  }
                );

                return (
                  <React.Fragment>
                    <div
                      className={objectiveNameClass}
                      style={objectiveNameStyle}
                    >
                      {nonLeafObjective.label}
                    </div>
                    <div className={tableWrapperClasses}>
                      <table
                        className={innerContainerClasses}
                        style={innerContainerStyle}
                      >
                        {this.getHeaderRow()}
                        <tbody>
                          {_.map(objectives, (objective, index) => {
                            return this.getTableRow({
                              objective,
                              isEvaluation,
                              index,
                              isLastRow: index === objectives.length - 1,
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                    {(isEvaluationEnable || isEvaluated) &&
                    (userType === "staff" || showRatingContainerInViewMode) ? (
                      <div
                        className={classes.ratingContainer}
                        style={
                          showJustification
                            ? styles.marginBottom
                            : rubricRatingContainerStyle
                        }
                      >
                        <div
                          className={classes.ratingTextContainer}
                          style={rubricRatingTextContainerStyle}
                        >
                          {!showRevampedEvaluationUI ? (
                            <div className="text-label">
                              {t("unitPlan:best_fit_rating_for_text_normal", {
                                label: _.get(nonLeafObjective, "label", ""),
                              })}
                              {userType === "staff" ? (
                                <div className={classes.alertIconContainer}>
                                  <LinkWithTooltip
                                    tooltip={t(
                                      userType === "staff"
                                        ? "classRoom:rubric_rating_tooltip"
                                        : "classRoom:rubric_rating_tooltip_for_reader"
                                    )}
                                    href="#"
                                    tooltipContainerStyle={
                                      styles.tooltipContainerStyle
                                    }
                                    placement="right"
                                  >
                                    <InfoIcon
                                      width={"16px"}
                                      height={"16px"}
                                      fill={colors.neutral400}
                                    />
                                  </LinkWithTooltip>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        <div
                          className={classNames(
                            { [classes.ratingCompContainer]: true },
                            {
                              [classes.disabled]:
                                !isEvaluationEnable && !showOnlyDefaultCursor,
                            }
                          )}
                          style={rubricRatingCompContainerStyle}
                        >
                          {isGeneratingAIResponse ? (
                            <div className={classes.skeletonContainer}>
                              {this.renderSkeletonLoader({
                                count: _.size(academicCriteriaValues),
                              })}
                            </div>
                          ) : (
                            <CriteriaRating
                              shouldUpdateOnValueChnage={true}
                              isEvaluation={isEvaluationEnable}
                              options={academicCriteriaValues}
                              value={_.get(
                                selectedCriteria,
                                "academicCriteriaValue.id",
                                ""
                              )}
                              isViewMode={showRatingContainerInViewMode}
                              onCriteriaClick={handleCriteriaClick}
                              customSelectedStyle={styles.customSelectedStyle}
                              timeOut={
                                parentType === "STUDENT_ASSIGNMENT" ? 0 : 1000
                              }
                              containerStyle={
                                criteriaRatingElementsContainerStyle
                              }
                              customRatingElementClass={
                                customRatingElementClass
                              }
                              customSelectedRatingElement={
                                isAISelectedCriteria &&
                                classes.customAISelectedRatingElement
                              }
                              isGeneratingAIResponse={isGeneratingAIResponse}
                              isAISelectedCriteria={isAISelectedCriteria}
                            />
                          )}
                        </div>
                      </div>
                    ) : null}
                    {isStaff &&
                    isAiEvaluationEnabled &&
                    justificationMessage ? (
                      <AIJustification
                        justifications={[justificationMessage]}
                        isAIGeneratingResponse={isGeneratingAIResponse}
                        aiEvaluationLabel={aiJustificationlabel}
                      />
                    ) : null}
                  </React.Fragment>
                );
              });
            })}
          </React.Fragment>
        ) : (
          <tbody>
            {_.map(children, child => {
              _.map(child, objective => {
                return this.getTableRow({ objective, isEvaluation });
              });
            })}
          </tbody>
        );
      }
    );
  };

  writeAssessmentToolElement = ({ id, type, data }) => {
    const { updateField, portalType } = this.props;

    this.debouncedWriteRubricElementLocal({ id, type, data });
    updateField({
      key: id,
      params: { id, type, data, portalType },
      type: "ASSESSMENT_TOOL_ELEMENT",
    });
  };

  updateInputRef = ({ ref, refKey }) => {
    this.cellRefs[refKey] = ref;
  };

  setFocusedCell = refKey => {
    this.focusedCell = refKey;
  };

  getTableRow = ({ objective, index: rowIndex, isLastRow = false }) => {
    const {
      achievementLevels,
      assessmentTool,
      mode,
      t,
      isEvaluationEnable,
      updateEvaluationCell,
      toolEvaluationResponses,
      isEvaluatedByStudent,
      userType,
      isEvaluation,
      showInsight,
      insights,
      insightColor,
      cellStyle = {},
      assignmentId,
      saveAssessmentToolUpdateStatusInRedux,
      textAreaCustomStyles = {},
      headerColumnCellStyles = {},
      textAreaContainerStyle = {},
      showRevampedEvaluationUI,
      showOnlyDefaultCursor = false,
      lastRowCellStyles = {},
      onAcceptAIEvaluation,
      isAiEvaluationEnabled,
      isGeneratingAIResponse,
    } = this.props;

    const { id, label, isLeaf, subText } = objective;
    const { descriptors = [] } = assessmentTool;
    if (!isLeaf && isEvaluation) {
      return null;
    }

    const groupedToolEvaluationResponse = _.groupBy(
      toolEvaluationResponses,
      toolEvaluationResponse => toolEvaluationResponse.creatorType
    );

    const staffToolEvaluationResponses = _.get(
      groupedToolEvaluationResponse,
      "STAFF",
      []
    );

    const studentToolEvaluationResponses = _.get(
      groupedToolEvaluationResponse,
      "STUDENT",
      []
    );

    const aiToolEvaluationResponses = _.get(
      groupedToolEvaluationResponse,
      "AI",
      []
    );

    const headerColumnCellClass = classNames(
      { [classes.headerColumnCell]: true },
      { [classes.mergedCell]: !isLeaf },
      { [classes.headerColumnCellV2]: mode === "view" && isAiEvaluationEnabled }
    );
    return (
      <tr key={id}>
        {showRevampedEvaluationUI && isEvaluationEnable ? (
          <td className={classes.cellWrapper}>
            <div
              className={headerColumnCellClass}
              colSpan={isLeaf ? 1 : achievementLevels.length + 1}
              style={headerColumnCellStyles}
            >
              {label}
            </div>
          </td>
        ) : (
          <td
            className={headerColumnCellClass}
            colSpan={isLeaf ? 1 : achievementLevels.length + 1}
            style={{
              ...headerColumnCellStyles,
              ...(isLastRow ? lastRowCellStyles : {}),
            }}
          >
            {label}
          </td>
        )}
        {isLeaf &&
          _.map(achievementLevels, (level, columnIndex) => {
            const descriptor =
              _.find(descriptors, descriptor => {
                return (
                  descriptor.objective.id == objective.id &&
                  descriptor.achievementLevel.id == level.id
                );
              }) || {};

            const objectiveId = _.get(descriptor, "objective.id", "");
            const achievementLevelId = _.get(
              descriptor,
              "achievementLevel.id",
              ""
            );

            const updatedDescriptorLabel = getStyleStrippedText({
              text: descriptor.label,
            }).replace(/<br \/>/g, "<br>");

            const isSelected =
              _.findIndex(
                staffToolEvaluationResponses,
                response =>
                  response.rowId == objectiveId &&
                  response.columnId == achievementLevelId
              ) >= 0;
            const isStudentSelected =
              _.findIndex(
                studentToolEvaluationResponses,
                response =>
                  response.rowId == objectiveId &&
                  response.columnId == achievementLevelId
              ) >= 0 && isEvaluatedByStudent;

            const isAISelected =
              _.findIndex(
                aiToolEvaluationResponses,
                response =>
                  response.rowId == objectiveId &&
                  response.columnId == achievementLevelId &&
                  !response.isDeleted
              ) >= 0;

            const responseCount = _.get(
              _.find(
                insights,
                insight =>
                  insight.rowId == objectiveId &&
                  insight.columnId == achievementLevelId
              ),
              "responseCount",
              0
            );

            const studentArray = _.get(
              _.find(
                insights,
                insight =>
                  _.isEqual(
                    _.get(insight, "rowId", ""),
                    _.get(descriptor, "objective.id", "")
                  ) &&
                  _.isEqual(
                    _.get(insight, "columnId"),
                    _.get(descriptor, "achievementLevel.id")
                  )
              ),
              "studentsList",
              []
            );

            const cellClass = classNames(
              {
                [classes.disabled]:
                  !isEvaluationEnable && !showOnlyDefaultCursor,
              },
              {
                [classes.defaultCursor]: !isEvaluationEnable && mode === "edit",
              },
              {
                [classes.bodyRowCell]: !(
                  showRevampedEvaluationUI && isEvaluationEnable
                ),
                [classes.bodyRowCellV2]:
                  (showRevampedEvaluationUI && isEvaluationEnable) ||
                  (mode === "view" && isAiEvaluationEnabled),
              },
              {
                [classes.clickableCell]:
                  isEvaluationEnable &&
                  userType == "staff" &&
                  !isAiEvaluationEnabled,
              },
              {
                [classes.clickableCellV2]:
                  isEvaluationEnable && userType == "staff",
              },
              {
                [classes.clickableStudentCell]:
                  isEvaluationEnable &&
                  userType == "student" &&
                  !showRevampedEvaluationUI,
                [classes.clickableStudentCellV2]:
                  isEvaluationEnable &&
                  userType == "student" &&
                  showRevampedEvaluationUI,
              },
              {
                [classes.selectableCell]:
                  isSelected &&
                  !isAiEvaluationEnabled &&
                  !showRevampedEvaluationUI,
                [classes.selectableCellV2]:
                  isSelected &&
                  showRevampedEvaluationUI &&
                  !isAiEvaluationEnabled,
                [classes.selectableCellV3]:
                  isSelected &&
                  !isGeneratingAIResponse &&
                  !showRevampedEvaluationUI &&
                  (isAiEvaluationEnabled ? !isAISelected : true),
              },
              {
                [classes.aiSelectedCell]:
                  isAISelected && !isGeneratingAIResponse,
              },
              {
                [classes.selectableStudentCell]:
                  !isSelected && isStudentSelected && !showRevampedEvaluationUI,
                [classes.selectedStudentEvaluationOpenCell]:
                  !isSelected &&
                  isStudentSelected &&
                  showRevampedEvaluationUI &&
                  isEvaluationEnable,
                [classes.selectedStudentEvaluationClosedCell]:
                  !isSelected &&
                  isStudentSelected &&
                  showRevampedEvaluationUI &&
                  !isEvaluationEnable,
                [classes.generatingAIResponseCell]:
                  isGeneratingAIResponse && isAiEvaluationEnabled,
              }
            );

            const refKey = `Descriptor-${_.get(descriptor, "id", "")}`;

            const updateCellRef = ref => {
              this.evaluationCellRefs[`${rowIndex}_${columnIndex}`] = ref;
            };

            const handleClickEvaluation = async () => {
              const rowId = _.get(descriptor, "objective.id", "");
              const columnId =
                (userType == "staff" && isSelected) ||
                (userType == "student" && isStudentSelected)
                  ? null
                  : descriptor.achievementLevel.id;

              const visibleAIResponse =
                isAiEvaluationEnabled &&
                userType == "staff" &&
                _.find(
                  aiToolEvaluationResponses,
                  response => response.rowId === rowId && !response.isDeleted
                );

              if (isEvaluationEnable) {
                if (rowId) {
                  const isDeleteAiRatings =
                    !_.isEmpty(columnId) && !_.isEmpty(visibleAIResponse);

                  updateEvaluationCell({
                    rowId,
                    columnId,
                    isSelected,
                    isDeleteAiRatings,
                  });

                  if (isDeleteAiRatings) {
                    onAcceptAIEvaluation({
                      rowId,
                      columnId,
                    });
                  }
                }
              }
            };

            const handleKeyDown = e => {
              if (!isEvaluationEnable) {
                return;
              }

              switch (e?.key) {
                case "Enter":
                case " ":
                  e?.preventDefault();
                  handleClickEvaluation();
                  break;
                case "ArrowRight":
                  this.evaluationCellRefs[
                    `${rowIndex}_${columnIndex + 1}`
                  ]?.focus();
                  break;
                case "ArrowLeft":
                  this.evaluationCellRefs[
                    `${rowIndex}_${columnIndex - 1}`
                  ]?.focus();
                  break;
                case "ArrowDown":
                  this.evaluationCellRefs[
                    `${rowIndex + 1}_${columnIndex}`
                  ]?.focus();
                  break;
                case "ArrowUp":
                  this.evaluationCellRefs[
                    `${rowIndex - 1}_${columnIndex}`
                  ]?.focus();
                  break;
              }
            };

            const renderTextArea = () => {
              return (
                <UIBaseContentEditable
                  value={updatedDescriptorLabel}
                  style={_.merge(textAreaStyles.cell)}
                  textStyle={{
                    ..._.merge(textAreaStyles.cell),
                    ...textAreaCustomStyles,
                  }}
                  updateInputRef={ref => this.updateInputRef({ ref, refKey })}
                  onBlurInputField={() => {
                    this.setFocusedCell(null);
                    this.debouncedWriteRubricElementLocal?.flush();
                  }}
                  onFocusInputField={() => this.setFocusedCell(refKey)}
                  name={"label"}
                  disabled={
                    _.includes(descriptor.id, "NEW") || _.isEmpty(descriptor.id)
                  }
                  mode={mode}
                  placeholder={t("common:click_to_add_withLabel", {
                    label: t("common:definition"),
                  })}
                  emptyText={t("common:rubric_empty_text")}
                  allowEnter={true}
                  shouldPasteAsPlainText={false}
                  shouldParseInPlainText={false}
                  updateInputField={params => {
                    this.writeAssessmentToolElement({
                      id: descriptor.id,
                      data: params,
                      type: "MYP_OBJECTIVE_DESCRIPTOR",
                    });
                    saveAssessmentToolUpdateStatusInRedux({
                      assignmentId,
                      toolType: "MYP_OBJECTIVE_RUBRIC",
                    });
                  }}
                  containerStyle={textAreaContainerStyle}
                />
              );
            };

            const renderCellContent = () => {
              return (
                <>
                  {(isSelected || isStudentSelected || isAISelected) &&
                  !showRevampedEvaluationUI ? (
                    <div className={classes.tickSvgContainer}>
                      {isStudentSelected && (
                        <div
                          style={{ marginRight: isSelected ? "8px" : "0px" }}
                          className={classes.tickSvgComponent}
                        >
                          <TickCircleFilled size={"xx-small"} />
                        </div>
                      )}

                      {/* Teacher or AI */}
                      {isSelected &&
                      (isAiEvaluationEnabled ? !isAISelected : true) ? (
                        <div className={classes.tickSvgComponent}>
                          <TickCircleFilled
                            size={"xx-small"}
                            variant={"link"}
                          />
                        </div>
                      ) : isAISelected ? (
                        <div className={classes.tickSvgComponent}>
                          <TickCircleFilled
                            size={"xx-small"}
                            style={styles.aiTick}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {!showInsight ? (
                    showRevampedEvaluationUI ? (
                      <div className={classes.textAreaWithTickIcon}>
                        {renderTextArea()}
                        {isSelected || isStudentSelected ? (
                          <div className={classes.tickIconContainer}>
                            {isStudentSelected ? (
                              <TickCircleFilled
                                size={
                                  isEvaluationEnable ? "xx-small" : "xxx-small"
                                }
                              />
                            ) : null}
                            {isSelected ? (
                              <TickCircleFilled
                                size={
                                  isEvaluationEnable ? "xx-small" : "xxx-small"
                                }
                                variant={"link"}
                              />
                            ) : null}
                          </div>
                        ) : isEvaluationEnable ? (
                          <div className={classes.tickIconContainer}>
                            <CircleOutlined
                              size={"xx-small"}
                              variant="subtle"
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      renderTextArea()
                    )
                  ) : (
                    <div style={{ ...textAreaStyles.cell }}>
                      <MoreComponent
                        responseCount={responseCount}
                        studentArray={studentArray}
                        insightColor={insightColor}
                        t={t}
                      />
                      <div
                        className={classes.insightDescriptor}
                        dangerouslySetInnerHTML={{
                          __html: updatedDescriptorLabel,
                        }}
                      />
                    </div>
                  )}
                </>
              );
            };

            if (isGeneratingAIResponse) {
              return (
                <td className={cellClass} style={cellStyle}>
                  <SkeletonLoader height="100%" width="100%" key={level.id} />
                </td>
              );
            }

            if (showRevampedEvaluationUI && isEvaluationEnable) {
              return (
                <td className={classes.cellWrapper}>
                  <div
                    ref={updateCellRef}
                    onClick={handleClickEvaluation}
                    onKeyDown={handleKeyDown}
                    tabIndex={isEvaluationEnable ? 0 : null}
                    className={cellClass}
                    style={cellStyle}
                  >
                    {renderCellContent()}
                  </div>
                </td>
              );
            }

            return (
              <td
                key={level.id}
                ref={updateCellRef}
                className={cellClass}
                style={cellStyle}
                onClick={handleClickEvaluation}
                onKeyDown={handleKeyDown}
                tabIndex={isEvaluationEnable ? 0 : null}
              >
                {renderCellContent()}
              </td>
            );
          })}
      </tr>
    );
  };
  render() {
    const {
      mode,
      isEvaluation,
      t,
      rubricTableContainerStyle = {},
      noteContainerStyle = {},
    } = this.props;
    return (
      <div
        className={classes.container}
        style={
          mode == "view"
            ? { marginTop: 0, ...rubricTableContainerStyle }
            : rubricTableContainerStyle
        }
      >
        {mode == "edit" && (
          <div className={classes.noteContainer} style={noteContainerStyle}>
            <span className={classes.noteLabel}>{`${t("common:note")}:`}</span>
            <span>{t("unitPlan:rubric_note")}</span>
          </div>
        )}
        {!isEvaluation ? (
          <div className={classes.tableWrapper}>
            <table className={classes.innerContainer}>
              {this.getHeaderRow()}
              {this.getTableBody()}
            </table>
          </div>
        ) : (
          this.getTableBody()
        )}
      </div>
    );
  }
}

const mapActionCreators = {
  writeAssessmentToolElementLocal,
  updateField,
  getCriteriaSetOfPypType,
  createPypElementRating,
  createIbPypElementRating,
  deletePypElementRating,
  saveAssessmentToolUpdateStatusInRedux,
};

const _getSeggregatedObjectives = ({ isEvaluation, objectives }) => {
  if (isEvaluation) {
    const objectivesObj = _.keyBy(objectives, "id");
    const seggregatedObjectives = _.reduce(
      objectives,
      (result, currObj) => {
        if (!currObj.isLeaf) {
          const groupedObjectives = [];
          groupedObjectives.push(currObj);
          const { children } = currObj;
          _.forEach(children, ({ id: childrenId }) => {
            if (objectivesObj[childrenId]) {
              groupedObjectives.push(objectivesObj[childrenId]);
            }
          });
          result.push(groupedObjectives);
        }
        return result;
      },
      []
    );
    return seggregatedObjectives;
  }
  return [objectives];
};

const getSeggregatedObjectives = _.memoize(
  params => _getSeggregatedObjectives(params),
  params => JSON.stringify(params)
);

const _flattenParentChild = nodes => {
  const result = [];

  function flatten(nodes) {
    _.forEach(nodes, node => {
      if (node?.depth !== 1) result.push(node);
      flatten(node?.children);
    });
  }

  flatten(nodes);
  return result;
};

const flattenParentChild = _.memoize(
  params => _flattenParentChild(params),
  params => JSON.stringify(params)
);

const mapStateToProps = (state, ownProps) => {
  const { nestedObjectives = [], isEvaluation } = ownProps;

  const currentCurriculumProgramId = getSelectedCurriculumId(state);

  /*
  nestedObjectives = [
    {
      label:"English",
      children:[
        {
          Label:"Listening",
          children: [...]
        }
      ]
    }
  ]

  flattenNodes = [
    {
      Label: "English",
    },
    {
      Label: "Listening"
    }
  ]

  seggregatedObjectives = [
    {
      label:"English",
      children: [
        [[...],[...],[...]],
        [[...],[...]]
      ]
    }
  ]
  */
  const seggregatedObjectives = {};
  _.forEach(nestedObjectives, val => {
    const flattenNodes = flattenParentChild([val]);

    // Zero depth node contains the subject label
    let zeroDepthNode = {};
    const remainingNodes = [];
    _.forEach(flattenNodes, node => {
      if (node?.depth === 0) {
        zeroDepthNode = node;
      } else {
        remainingNodes.push(node);
      }
    });

    const remainingNodesChildren = [];
    _.forEach(remainingNodes, node => {
      if (!seggregatedObjectives[zeroDepthNode?.id]) {
        seggregatedObjectives[zeroDepthNode?.id] = {
          id: zeroDepthNode?.id,
          label: zeroDepthNode?.label,
          children: [],
        };
      }

      remainingNodesChildren.push(node);
    });

    seggregatedObjectives[zeroDepthNode?.id]?.children?.push(
      getSeggregatedObjectives({
        objectives: remainingNodesChildren,
        isEvaluation,
      })
    );
  });

  const academicYearId = getSelectedAcademicYearId(state);

  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    curriculumProgramId: currentCurriculumProgramId,
    academicYearId,
  });

  const mypIbDefinedGradeScale = readMYPIBDefinedGradeScaleFromCache({
    curriculumProgramId: currentCurriculumProgramId,
  });

  return {
    userType: state.login.userInfo.user_type,
    seggregatedObjectives,
    currentCurriculumProgramId,
    isUsingNewGradingSetupInCourses,
    mypIbDefinedGradeScale,
  };
};

export default compose(
  I18nHOC({ resource: ["unitPlan", "classRoom"] }),
  connect(mapStateToProps, mapActionCreators)
)(RubricTable);

//Need Review @kunal
